import React, { useState, useRef, useImperativeHandle } from 'react';
import { message, Spin } from 'antd';
import FormSegmentation from '../FormSegmentation/FormSegmentation'; 
import FormCompanyRelationship from '../FormCompanyRelationship/FormCompanyRelationship'; 
import FormProducts from '../FormProducts/FormProducts'; 
import FormEmployeeLocales from '../FormEmployeeLocales/FormEmployeeLocales'; 
import FormCustomerLocales from '../FormCustomerLocales/FormCustomerLocales'; 
import FormLearningProfiles from '../FormLearningProfiles/FormLearningProfiles'; 
import FormSaveCancelToolbar from '../FormSaveCancelToolbar/FormSaveCancelToolbar'; 
import { flags } from '../../../featureFlags/flags'; 
import { useFeatureFlags } from '../../../featureFlags';
import { authService } from '../../../auth/authService';
import './styles.less';

const TabAudience = ( { form, isSavingCallback, ref, loading: loadingObject } ) => {
    const formProductsRef = useRef();
    const formSegmentationRef = useRef();
    const formLearningProfilesRef = useRef();
    const formLocalesRef = useRef();
    const formEmployeeLocalesRef = useRef();
    const formCompanyRelationshipRef = useRef();
    const [ isSaving, setIsSaving ] = useState( false );
    const [ isModified, setIsModified ] = useState( false );
    const [ openConfirm, setOpenConfirm ] = useState( false );
    const [ showProducts ] = useFeatureFlags( [ flags.SEGMENTATION_PRODUCT ] );
    const [ showRosterGroups ] = useFeatureFlags( [ flags.SEGMENTATION_CLIENT_MANAGED ] );
    const [ showLocales ] = useFeatureFlags( [ flags.SEGMENTATION_LOCALES ] );
    const [ showLearningProfiles ] = useFeatureFlags( [ flags.SEGMENTATION_LEARNING_PROFILES ] );
    const [ showCompanyRelationship ] = useFeatureFlags( [ flags.SEGMENTATION_COMPANY_RELATIONSHIP ] );
    
    useImperativeHandle( ref, () => ( {
        isModified() {
            return isModified;
        },
        showSaveConfirm() {
            setOpenConfirm( true );
        }
    } ) );
 
    const handleSave = async () => {
        setIsSaving( true );
        isSavingCallback( true );
        setOpenConfirm( false ); 
        
        let hasError = false;

        if ( showRosterGroups && formSegmentationRef.current.isModified() ) {
            const result = await formSegmentationRef.current.syncData();
            
            if ( !result ) {
                hasError = true;
            }
        }

        if ( showCompanyRelationship && formCompanyRelationshipRef.current.isModified() ) {
            const result = await formCompanyRelationshipRef.current.syncData();

            if ( !result ) {
                hasError = true;
            }
        }
        
        if ( showProducts && formProductsRef.current.isModified() ) {
            const result = await formProductsRef.current.syncData();

            if ( !result ) {
                hasError = true;
            }
        }
        
        if ( showLearningProfiles && formLearningProfilesRef.current.isModified() ) {
            const result = await formLearningProfilesRef.current.syncData();

            if ( !result ) {
                hasError = true;
            }
        }
        
        if ( showLocales && formEmployeeLocalesRef.current.isModified() ) {
            const result = await formEmployeeLocalesRef.current.syncData();

            if ( !result ) {
                hasError = true;
            }
        }

        if ( showLocales && formLocalesRef.current.isModified() ) {
            const result = await formLocalesRef.current.syncData();

            if ( !result ) {
                hasError = true;
            }
        }

        if ( !hasError ) {
            message.success( 'Course saved.' );
        }

        setIsSaving( false );
        isSavingCallback( false );
    };

    const handleReset = () => {
        formProductsRef?.current?.reset();
        formSegmentationRef?.current?.reset();
        formLocalesRef?.current?.reset();
        formEmployeeLocalesRef?.current?.reset();
        formLearningProfilesRef?.current?.reset();
        formCompanyRelationshipRef?.current?.reset();
        setOpenConfirm( false ); 
        isSavingCallback( false );
    };
 
    const handleModifiedChange = () => { 
        if ( formSegmentationRef?.current?.isModified() || 
            formLocalesRef?.current?.isModified() ||
            formProductsRef?.current?.isModified() ||
            formLearningProfilesRef?.current?.isModified() ||
            formEmployeeLocalesRef?.current?.isModified() ||
            formCompanyRelationshipRef?.current?.isModified() ) {
            setIsModified( true );
        } else {
            setIsModified( false );
        }
    };

    return (
        <div className="audience-tab tab-main-columns">
            <div className="left">
                <Spin spinning={ isSaving }>
                    { showRosterGroups && !loadingObject &&
                        <FormSegmentation 
                            form={ form }
                            ref={ formSegmentationRef }
                            modifiedCallback={ handleModifiedChange } 
                        />
                    }
                    
                    { showCompanyRelationship &&
                        <FormCompanyRelationship 
                            form={ form }
                            ref={ formCompanyRelationshipRef }
                            modifiedCallback={ handleModifiedChange } 
                        />
                    }

                    { showProducts &&
                        <FormProducts 
                            form={ form }
                            ref={ formProductsRef }
                            modifiedCallback={ handleModifiedChange } 
                        />
                    }

                    { showLearningProfiles &&
                        <FormLearningProfiles 
                            form={ form }
                            ref={ formLearningProfilesRef }
                            modifiedCallback={ handleModifiedChange } 
                        />
                    }

                    { showLocales &&
                        <FormCustomerLocales 
                            form={ form }
                            ref={ formLocalesRef }
                            modifiedCallback={ handleModifiedChange } 
                        />
                    }

                    { showLocales && authService.isHoya() &&
                        <FormEmployeeLocales 
                            form={ form }
                            ref={ formEmployeeLocalesRef }
                            modifiedCallback={ handleModifiedChange } 
                        />
                    }
                </Spin>
            </div>

            <div className="right">
                <FormSaveCancelToolbar
                    form={ form } 
                    open={ openConfirm }
                    saving={ isSaving }
                    modified={ isModified }
                    saveCallback={ handleSave }
                    cancelCallback={ handleReset }
                />
            </div>
        </div>
    );
};

export default TabAudience;
